import { useTranslation } from "react-i18next"

import type { StyleAdherence } from "@/features/studio-ai/types/studio-ai.types"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"

import { ColorPicker } from "./color-picker"

type StyleTemplatePreviewProps = {
  title: string
  description: string
  promptFragment: string
  brandColors: string[]
  previewImages: string[]
  visibility: "personal" | "company"
  recommendedAdherence: StyleAdherence
  onTitleChange: (title: string) => void
  onDescriptionChange: (description: string) => void
  onPromptFragmentChange: (promptFragment: string) => void
  onBrandColorsChange: (colors: string[]) => void
  onVisibilityChange: (visibility: "personal" | "company") => void
  onRecommendedAdherenceChange: (adherence: StyleAdherence) => void
}

const ADHERENCE_OPTIONS: Array<{ value: StyleAdherence; labelKey: string }> = [
  { value: "inspired", labelKey: "creator.adherenceInspired" },
  { value: "balanced", labelKey: "creator.adherenceBalanced" },
  { value: "strict", labelKey: "creator.adherenceStrict" },
]

export function StyleTemplatePreview({
  title,
  description,
  promptFragment,
  brandColors,
  previewImages,
  visibility,
  recommendedAdherence,
  onTitleChange,
  onDescriptionChange,
  onPromptFragmentChange,
  onBrandColorsChange,
  onVisibilityChange,
  onRecommendedAdherenceChange,
}: StyleTemplatePreviewProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.styleTemplates" })

  const settingsService = SettingsService.getInstance()
  const isPromptAdmin = settingsService.settings.user_policy.is_prompt_admin

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-6">
      {/* Title and description */}
      <div className="space-y-2">
        <Label className="text-sm font-medium" htmlFor="style-template-title">
          {t("creator.templateName", { defaultValue: "Template name" })}
        </Label>
        <Input
          id="style-template-title"
          maxLength={120}
          value={title}
          placeholder={t("creator.templateNamePlaceholder", {
            defaultValue: "Name your style template",
          })}
          onChange={(event) => onTitleChange(event.target.value)}
        />
        <Label className="text-sm font-medium" htmlFor="style-template-description">
          {t("creator.templateDescription", { defaultValue: "Description" })}
        </Label>
        <Textarea
          id="style-template-description"
          maxLength={500}
          rows={3}
          value={description}
          placeholder={t("creator.templateDescriptionPlaceholder", {
            defaultValue: "Short description of this visual style",
          })}
          onChange={(event) => onDescriptionChange(event.target.value)}
        />
      </div>

      {/* Color palette */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">{t("creator.colorPalette")}</Label>
        <div className="flex flex-wrap items-center gap-2">
          {brandColors.map((color, i) => (
            <ColorPicker
              key={i}
              color={color}
              onChange={(newColor) => {
                const updated = [...brandColors]
                updated[i] = newColor
                onBrandColorsChange(updated)
              }}
              onRemove={() => {
                onBrandColorsChange(brandColors.filter((_, idx) => idx !== i))
              }}
            />
          ))}
          <ColorPicker
            color="#000000"
            onChange={(newColor) => {
              onBrandColorsChange([...brandColors, newColor])
            }}
          />
        </div>
      </div>

      {/* Prompt fragment */}
      <div className="space-y-2">
        <Label className="text-sm font-medium" htmlFor="style-template-prompt">
          {t("creator.promptPreview")}
        </Label>
        <Textarea
          id="style-template-prompt"
          placeholder={t("promptFragmentDesc")}
          rows={6}
          value={promptFragment}
          onChange={(event) => onPromptFragmentChange(event.target.value)}
        />
      </div>

      {/* Example images */}
      {previewImages.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">{t("creator.exampleImages")}</Label>
          <div className="grid grid-cols-2 gap-2">
            {previewImages.map((url, i) => (
              <img
                key={i}
                alt={`Example ${i + 1}`}
                className="aspect-square w-full rounded-lg border object-cover"
                src={url}
              />
            ))}
          </div>
        </div>
      )}

      {/* Recommended adherence */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">
          {t("creator.recommendedAdherence", { defaultValue: "Recommended strength" })}
        </Label>
        <p className="text-muted-foreground text-xs">
          {t("creator.recommendedAdherenceDesc", {
            defaultValue: "Default strength when this template is used",
          })}
        </p>
        <div className="flex gap-2">
          {ADHERENCE_OPTIONS.map((option) => (
            <Button
              key={option.value}
              className={cn(recommendedAdherence === option.value && "pointer-events-none")}
              size="sm"
              variant={recommendedAdherence === option.value ? "default" : "outline"}
              onClick={() => onRecommendedAdherenceChange(option.value)}
            >
              {t(option.labelKey, { defaultValue: option.value })}
            </Button>
          ))}
        </div>
      </div>

      {/* Visibility toggle */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">{t("creator.saveAs")}</Label>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={visibility === "personal" ? "default" : "outline"}
            onClick={() => onVisibilityChange("personal")}
          >
            {t("personal")}
          </Button>
          {isPromptAdmin && (
            <Button
              size="sm"
              variant={visibility === "company" ? "default" : "outline"}
              onClick={() => onVisibilityChange("company")}
            >
              {t("company")}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
